import { AlertCircle, RefreshCw, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  useCapacity,
  getUtilizationStatus,
  formatMinutesAsHours,
  type CapacityPeriod,
} from '../hooks/useCapacity';
import {
  SkillCapacityChart,
  getSkillAggregateUtilization,
  skillHasActivity,
} from '../components/SkillCapacityChart';

/**
 * Capacity Forecast page — demand vs capacity by skill, always showing 13 weeks.
 */
export function Capacity() {
  const { data, isLoading, isError, error, refetch, isFetching } = useCapacity({
    granularity: 'week',
  });

  if (isLoading) {
    return <CapacityLoading />;
  }

  if (isError) {
    return <CapacityError message={error.message} onRetry={() => refetch()} />;
  }

  const periods = data?.periods ?? [];

  // Collect unique skills that have any activity across all periods
  const skillMap = new Map<string, string>();
  for (const period of periods) {
    for (const skill of period.bySkill) {
      if (!skillMap.has(skill.skillId)) {
        skillMap.set(skill.skillId, skill.skillName);
      }
    }
  }

  // Sort skills by aggregate utilization descending
  const skills = Array.from(skillMap, ([skillId, skillName]) => ({
    skillId,
    skillName,
    utilization: getSkillAggregateUtilization(periods, skillId),
    hasActivity: skillHasActivity(periods, skillId),
  }))
    .filter((s) => s.hasActivity)
    .sort((a, b) => b.utilization - a.utilization);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Capacity Forecast</h2>
          <p className="text-sm text-foreground-muted mt-1">
            Demand vs capacity by skill &mdash; next 13 weeks
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-background-subtle transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {periods.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <SummaryCards periods={periods} />

          {skills.map((skill) => (
            <SkillCapacityChart
              key={skill.skillId}
              skillId={skill.skillId}
              skillName={skill.skillName}
              periods={periods}
            />
          ))}

          {skills.length === 0 && (
            <div className="text-center py-8 text-foreground-muted text-sm">
              No skills with scheduled work in this period.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCards({ periods }: { periods: CapacityPeriod[] }) {
  const totalAvailable = periods.reduce((sum, p) => sum + p.availableMinutes, 0);
  const totalDemanded = periods.reduce((sum, p) => sum + p.demandedMinutes, 0);
  const totalDemandMin = periods.reduce(
    (sum, p) => sum + p.bySkill.reduce((s, sk) => s + (sk.demandedMinutesMin ?? 0), 0),
    0
  );
  const totalDemandMax = periods.reduce(
    (sum, p) => sum + p.bySkill.reduce((s, sk) => s + (sk.demandedMinutesMax ?? 0), 0),
    0
  );
  const netMinutes = totalAvailable - totalDemanded;
  const utilization = totalAvailable > 0 ? Math.round((totalDemanded / totalAvailable) * 100) : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <SummaryCard
        title="Demand Range"
        value={`${formatMinutesAsHours(totalDemandMin)} – ${formatMinutesAsHours(totalDemandMax)}`}
        subtitle={`Mid: ${formatMinutesAsHours(totalDemanded)}`}
        icon={Minus}
      />
      <SummaryCard
        title="Total Capacity"
        value={formatMinutesAsHours(totalAvailable)}
        icon={Minus}
      />
      <SummaryCard
        title="Net Position"
        value={`${netMinutes >= 0 ? '+' : ''}${formatMinutesAsHours(Math.abs(netMinutes))}`}
        subtitle={`${utilization}% overall utilization`}
        status={getUtilizationStatus(utilization)}
        icon={netMinutes >= 0 ? TrendingDown : TrendingUp}
      />
    </div>
  );
}

function SummaryCard({
  title,
  value,
  subtitle,
  status,
  icon: Icon,
}: {
  title: string;
  value: string;
  subtitle?: string;
  status?: 'low' | 'optimal' | 'high' | 'overloaded';
  icon: typeof TrendingUp;
}) {
  const statusColors: Record<string, string> = {
    low: 'text-foreground-muted',
    optimal: 'text-success',
    high: 'text-warning',
    overloaded: 'text-danger',
  };
  const color = status ? statusColors[status] : 'text-foreground-muted';

  return (
    <div className="border border-border rounded-lg p-4 bg-background-subtle">
      <div className="flex items-center justify-between">
        <span className="text-sm text-foreground-muted">{title}</span>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <div className={`text-2xl font-semibold mt-1 ${status ? color : ''}`}>{value}</div>
      {subtitle && <div className="text-xs text-foreground-subtle mt-1">{subtitle}</div>}
    </div>
  );
}

function CapacityLoading() {
  return (
    <div className="space-y-6">
      <div>
        <div className="h-8 w-48 bg-background-muted rounded animate-pulse" />
        <div className="h-4 w-64 bg-background-muted rounded animate-pulse mt-2" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="border border-border rounded-lg p-4 bg-background-subtle">
            <div className="h-4 w-24 bg-background-muted rounded animate-pulse" />
            <div className="h-8 w-16 bg-background-muted rounded animate-pulse mt-2" />
          </div>
        ))}
      </div>
      <div className="border border-border rounded-lg p-6 bg-background">
        <div className="h-6 w-40 bg-background-muted rounded animate-pulse mb-4" />
        <div className="h-48 bg-background-muted rounded animate-pulse" />
      </div>
    </div>
  );
}

function CapacityError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <AlertCircle className="h-12 w-12 text-danger mb-4" />
      <h3 className="text-lg font-medium">Failed to load capacity data</h3>
      <p className="text-sm text-foreground-muted mt-1 max-w-md">{message}</p>
      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary-hover transition-colors text-sm font-medium"
      >
        Try Again
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center border border-border rounded-lg">
      <TrendingUp className="h-12 w-12 text-foreground-muted mb-4" />
      <h3 className="text-lg font-medium">No capacity data</h3>
      <p className="text-sm text-foreground-muted mt-1">
        Capacity data will appear once resources and schedules are configured.
      </p>
    </div>
  );
}
